import { NetworkStatus } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";
import { ViewStyle, useWindowDimensions } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useDebouncedCallback } from "use-debounce";

import TeamsView from "./TeamsView";
import TeamItem from "@/features/roster-management/components/molecules/TeamItem";

import { ListRenderItem } from "@/components/atoms/FlashList";
import { Spinner } from "@/components/atoms/Spinner";
import { spacingForDevicesWithoutNotch } from "@/config/constants";
import { useCurrentUser } from "@/features/auth";
import {
  RosterManagementTeamFragment,
  useRosterManagementListMyOrganizationsEligibleTeamsQuery,
} from "@/generated/graphql";
import useMobileScreen from "@/hooks/useMobileScreen";
import useSetPageTitle from "@/hooks/useSetPageTitle";

/**
 * Teams Container
 *
 * Manages team data fetching, search, and pagination for the roster
 * management team selection screen. Organizations associate with teams
 * to determine which players appear in their roster view.
 * @module features/roster-management/screens/Teams/TeamsContainer
 */
const TeamsContainer = () => {
  const currentUser = useCurrentUser();
  const { bottom } = useSafeAreaInsets();
  const [searchText, setSearchText] = useState<string>("");
  const isSmallScreen = useMobileScreen();
  const { data, fetchMore, refetch, loading, networkStatus } =
    useRosterManagementListMyOrganizationsEligibleTeamsQuery({
      variables: { search: searchText },
      notifyOnNetworkStatusChange: true,
    });

  const isRefreshing = useMemo(
    () => networkStatus === NetworkStatus.refetch,
    [networkStatus]
  );

  const teams = useMemo(
    () =>
      data?.listMyOrganizationsEligibleTeams?.edges.map(edge => edge.node) ??
      [],
    [data?.listMyOrganizationsEligibleTeams?.edges]
  );

  const endCursor = useMemo(
    () =>
      data?.listMyOrganizationsEligibleTeams?.pageInfo.hasNextPage
        ? data?.listMyOrganizationsEligibleTeams?.pageInfo.endCursor
        : null,
    [
      data?.listMyOrganizationsEligibleTeams?.pageInfo.endCursor,
      data?.listMyOrganizationsEligibleTeams?.pageInfo.hasNextPage,
    ]
  );

  const handleLoadMore = useCallback(() => {
    if (endCursor && !loading) {
      fetchMore({
        variables: { after: endCursor },
      });
    }
  }, [endCursor, fetchMore, loading]);

  const handleRefresh = useCallback(() => refetch().catch(() => {}), [refetch]);

  const handleSearch = useCallback((value: string) => {
    setSearchText(value);
  }, []);
  const handleSearchDebounce = useDebouncedCallback(handleSearch, 300);

  const renderItem: ListRenderItem<RosterManagementTeamFragment> = useCallback(
    ({ item }) => (
      <TeamItem
        item={item}
        organizationId={currentUser?.organizationId ?? ""}
      />
    ),
    [currentUser?.organizationId]
  );

  const renderListFooterComponent = useCallback(
    () =>
      loading && !isRefreshing && teams.length ? (
        <Spinner size="large" color="white" />
      ) : null,
    [loading, isRefreshing, teams.length]
  );

  const keyExtractor = useCallback(
    (item: RosterManagementTeamFragment) => item.id,
    []
  );

  const { width } = useWindowDimensions();
  // Replaces useBreakpointValue({ default: width, md: isSmallScreen ? 750 : 640 }):
  // cap at the md breakpoint (≥768px) and use full width below it.
  const widthContent = width >= 768 ? (isSmallScreen ? 750 : 640) : width;

  const contentContainerStyle = useMemo<ViewStyle>(
    () => ({
      width: widthContent,
      paddingBottom: bottom + spacingForDevicesWithoutNotch,
    }),
    [widthContent, bottom]
  );

  const refreshControlColors = useMemo<string[]>(() => ["white"], []);

  useSetPageTitle("Team Selection");

  return (
    <TeamsView
      teams={teams}
      loading={loading}
      refreshing={isRefreshing}
      renderItem={renderItem}
      renderListFooterComponent={renderListFooterComponent}
      onLoadMore={handleLoadMore}
      onRefresh={handleRefresh}
      onSearch={handleSearchDebounce}
      keyExtractor={keyExtractor}
      contentContainerStyle={contentContainerStyle}
      refreshControlColors={refreshControlColors}
    />
  );
};

export default TeamsContainer;
